/* -----------------Globals--------------- */
import { useCallback, useEffect, useMemo, useState } from "react";

/* -----------------UI--------------- */

import { isWeb } from "@helpers/platform";
/* -----------------Helpers & Hooks--------------- */
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
import { getUserPrompt } from "../components/QASearch/helpers";
import { useAilaStore } from "../hooks/useAilaStore";
import useSessionStore from "../hooks/useSessionStore";

import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useFocusEffect, useLocalSearchParams } from "expo-router";
import { Chip } from "react-native-paper";
import QAChipsWrapper from "../components/QASearch/QAChipsWrapper";

import { useAppStore } from "@hooks/useAppStore";
/* -----------------Child components--------------- */
import PostAnswerModal from "../components/postanswer/PostAnswerModal";

import { isAiSubmittable, sessionMaxWidth } from "../constants";
import { getMaximumScore } from "../helpers/grading";
import { getParentStem, replaceBrackets } from "../hooks/useAIFeedback";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { interpolateString } from "@helpers/string-utils";
import { useCopyToClipboard } from "@hooks/useCopyToCllipboard";
import useTutorialCache from "@hooks/useTutorialCache";
import type { OriginalQuestionData } from "@memorang/features/src/qa-search/types/question";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import { useDevice } from "@memorang/ui/useDevice";
import { useTranslation } from "react-i18next";
import AiSessionFab from "../components/AiSessionFab";
import { calculateXp } from "../components/postanswer/helper";
import { getIntlExplanationForChoice } from "../helpers/session";
import useCurrentSession from "../hooks/useCurrentSession";

const StudySessionFeedbackContainer = ({
	hasParentStem,
}: {
	hasParentStem?: boolean;
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}) => {
	const { t } = useTranslation();

	const { index } = useLocalSearchParams<{
		index: string;
		prev: string;
	}>();

	const [showSuggestCorrectionDialog, setShowSuggestCorrectionDialog] =
		useState(false);

	const [showAiDisclaimer, setShowAiDisclaimer] = useState(false);

	const { isMobile, isDesktopWeb } = useDevice();

	const { tutorialData, setTutorialData } = useTutorialCache();

	const aiDisclaimer = useAppStore(
		(state) => state.app.aiAssistant?.disclaimer,
	);

	const shouldShowAiDisclaimer = Boolean(
		aiDisclaimer?.enabled && !tutorialData?.hasSeenAiDisclaimer,
	);

	const { handleSuggestCorrectionNavigation } = useCustomNavigation();
	const {
		currentExamContext: { examName },
	} = useExamContext();

	const toggleShowSuggestCorrectionDialog = () => {
		setShowSuggestCorrectionDialog((prev) => !prev);
	};
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const answers = useSessionStore((state) => state.answers);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);

	const { copyToClipboard } = useCopyToClipboard();
	const { currentItem } = useCurrentSession();
	const aiFeedback = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.explanation,
	);
	const aiTranscription = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.transcription,
	);
	const sessionView = useAppStore((state) => state.sessionView);

	const attempts = useSessionStore((state) => state.attempts);

	const currentAttempt = attempts.get(`${currentItem.id!}`);

	const attemptKeys = Object.keys(currentAttempt?.choiceAttempts || {});

	const numAttempts = attemptKeys.length;

	const showXpEarned = sessionView?.postAnswerStats?.showXpEarned;
	const showNumAttempts = sessionView?.postAnswerStats?.showNumAttempts;

	const { content, metadata, stimulusMetadata } = currentItem;
	const choices =
		currentItem.response.type === "choice" ? currentItem.response.choices : [];
	const stem = content?.stem!;
	const type = metadata?.type!;
	const stimulusType = stimulusMetadata?.type;

	const isAiItemType = isAiSubmittable(currentItem.response.type);
	const isStreaming = useAIFeedbackStore((store) => store.streaming);
	const topics = currentItem.tags?.map((tag) => tag.label ?? "") || [];
	const itemId = currentItem.id!;

	const currentAnswer = answers?.get(currentItem.id!) || currentItem.answer;

	const currentUserResponse = userResponse?.[itemId] as string | undefined;

	const combinedCitations = useMemo(() => {
		return (
			metadata?.citations?.sort((a, b) => {
				if (a.url && !b.url) {
					return -1;
				}
				if (!a.url && b.url) {
					return 1;
				}
				return 0;
			}) ?? []
		);
	}, [metadata]);
	const numberOfWords =
		type === "ACADEMIC_DISCUSSION_RESPONSE" ||
		type === "INTEGRATED_WRITING_RESPONSE" ||
		type === "WRITING_EMAIL"
			? currentUserResponse?.trim().split(/\s+/).length || 0
			: undefined;

	const updateIsAssistantVisible = useAilaStore(
		(state) => state.updateShowDrawer,
	);
	const updateSelectedQuery = useAilaStore(
		(state) => state.updateSelectedQuery,
	);

	const aiAssistantTitle = useAppStore((store) => store.app.aiAssistant.title);

	const lastAttemptChoiceId = attemptKeys[attemptKeys.length - 1];

	const numResponses = choices.find(
		(choice) => choice.id === lastAttemptChoiceId,
	)?.numFirstSelections;

	useFocusEffect(
		useCallback(() => {
			if (!isDesktopWeb) {
				updateIsAssistantVisible(false);
				updateSelectedQuery("");
			}
		}, [updateIsAssistantVisible, updateSelectedQuery, isDesktopWeb]),
	);

	useEffect(() => {
		if (isSummativeTest && inReviewMode) {
			updateIsAssistantVisible(true);
		}
	}, [isSummativeTest, inReviewMode, updateIsAssistantVisible]);

	const handleShowAiDisclaimer = () => {
		setShowAiDisclaimer(true);
	};
	const handleCloseAiDisclaimer = () => {
		setShowAiDisclaimer(false);
	};

	const renderSuggestCorrectionButton = () => {
		if (!isSummativeTest) {
			return (
				<Box alignSelf="center" marginTop={16} paddingBottom={100}>
					<Button
						onPress={() =>
							handleSuggestCorrectionNavigation(
								toggleShowSuggestCorrectionDialog,
								`${currentItem.id!}`,
							)
						}
						icon={"flag-outline"}
					>
						{t("session.suggestCorrection")}
					</Button>
					{showSuggestCorrectionDialog && (
						<FeedbackDialogContainer
							visible={showSuggestCorrectionDialog}
							onDismiss={toggleShowSuggestCorrectionDialog}
							title={t("dialog.suggestCorrection.title")}
							subtitle={t("dialog.suggestCorrection.body")}
							icon="flag-outline"
							cta={t("dialog.suggestCorrection.cta")}
							itemId={`${currentItem.id!}`}
							sessionType={isSummativeTest ? "SUMMATIVE" : "FORMATIVE"}
							type="Content Flagged"
						/>
					)}
				</Box>
			);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const renderCommonPostAnswerModal = useMemo(() => {
		const shouldRenderPostAnswerModal = Boolean(
			isSummativeTest ? inReviewMode : currentAnswer != null,
		);

		const shouldRenderFollowUps =
			!!shouldRenderPostAnswerModal && isWeb && !(isAiItemType && isStreaming);

		const confidence = currentAnswer?.confidence;
		const timeTaken = currentAnswer?.timeTaken;
		const correct = currentAnswer?.correct;

		const maximumScore = getMaximumScore(currentItem.scoring);

		const correctChoices = choices?.filter(({ isCorrect }) => isCorrect);

		const selectedChoice = currentAnswer?.selectedChoiceIds?.map((choiceId) => {
			const choice = choices.find((choice) => choice.id === choiceId);
			return {
				isCorrect: choice?.isCorrect,
				stem: choice?.text,
				text: choice?.text,
			};
		});
		const data = {
			stem: stem?.text ?? "",
			explanation: isAiItemType
				? (aiFeedback ?? "")
				: (getIntlExplanationForChoice(correctChoices[0]) ?? ""),
			choices,
			topics,
			userResponse: isAiItemType
				? (aiTranscription ?? currentUserResponse)
				: undefined,
		};
		const userPrompt = replaceBrackets(
			`${getParentStem(currentItem)}\n\n${getUserPrompt(data, examName)}`,
		);
		const originalQuestionData: OriginalQuestionData = {
			stem: data.stem,
			explanation: data?.explanation ?? "",
			topics,
			choices:
				data.choices.map((choice) => ({
					text: choice.text,
					isCorrect: choice.isCorrect,
				})) ?? [],
			selectedChoice: selectedChoice ?? [],
		};

		const numTotalAttempts = choices.length || 0;

		const attemptsStatValue = `${numAttempts} of ${numTotalAttempts}`;
		const xpEarned = calculateXp(numAttempts, false);
		return (
			<>
				<AiSessionFab
					itemId={currentItem.id!}
					shouldRender={shouldRenderPostAnswerModal}
					shouldShowAiDisclaimer={shouldShowAiDisclaimer}
					handleShowAiDisclaimer={handleShowAiDisclaimer}
				/>
				{shouldRenderPostAnswerModal && (
					<PostAnswerModal
						itemCriteria={
							currentItem.scoring?.criteria
								? Object.values(currentItem.scoring.criteria)
								: undefined
						}
						choices={choices}
						timeTaken={timeTaken ?? 0}
						confidence={confidence}
						showExplanation
						correct={correct}
						feedback={currentItem.feedback}
						tags={currentItem.tags}
						showXpEarned={showXpEarned}
						currentQuestionAnswered
						currentItemIndex={currentItemIndex}
						itemId={currentItem.id!}
						attemptsStatValue={attemptsStatValue}
						userPrompt={userPrompt}
						numResponses={numResponses}
						showNumAttempts={showNumAttempts}
						xpEarned={xpEarned}
						isSummativeTest={isSummativeTest}
						renderSuggestCorrectionButton={renderSuggestCorrectionButton}
						originalQuestionData={originalQuestionData}
						responseType={currentItem.response.type}
						numberOfWords={numberOfWords}
						maximumScore={maximumScore ?? 0}
						originalItemType={type}
						currentItemAnswer={currentItem.answer}
						stimulusType={stimulusType}
						citations={combinedCitations}
					/>
				)}
				<Box paddingVertical={16} gap={16}>
					{(!isSummativeTest || inReviewMode) && (
						<>
							<QAChipsWrapper
								itemId={currentItem.id!}
								userPrompt={userPrompt}
								shouldRender={shouldRenderFollowUps}
								originalQuestionData={originalQuestionData}
								loading={isAiItemType && isStreaming}
								shouldShowAiDisclaimer={shouldShowAiDisclaimer}
								handleShowAiDisclaimer={handleShowAiDisclaimer}
							/>
							{shouldRenderFollowUps && (
								<Chip
									compact
									onPress={() =>
										copyToClipboard(
											`${itemId}`,
											`Item #${itemId} copied to clipboard`,
										)
									}
									style={{
										alignSelf: "baseline",
									}}
								>
									Item: #{itemId}
								</Chip>
							)}
						</>
					)}
				</Box>
			</>
		);
	}, [
		answers,
		currentItem,
		currentItemIndex,
		examName,
		inReviewMode,
		isSummativeTest,
		shouldShowAiDisclaimer,
	]);

	return (
		<Box
			maxWidth={
				isSummativeTest ? sessionMaxWidth.summative : sessionMaxWidth.formative
			}
			alignSelf={
				hasParentStem ? undefined : isSummativeTest ? "flex-start" : "center"
			}
			paddingHorizontal={isMobile ? 16 : 0}
			width="100%"
		>
			{renderCommonPostAnswerModal}
			{isWeb && currentAnswer != null && renderSuggestCorrectionButton()}
			{shouldShowAiDisclaimer && (
				<ConfirmDialog
					visible={showAiDisclaimer}
					dismissCallback={handleCloseAiDisclaimer}
					title={aiDisclaimer?.title ?? ""}
					body={interpolateString(aiDisclaimer?.body ?? "", {
						agentName: aiAssistantTitle,
					})}
					confirmCallback={() => {
						setShowAiDisclaimer(false);
						setTutorialData({ hasSeenAiDisclaimer: true });
					}}
					confirmCta="Got it"
					icon="alert-outline"
				/>
			)}
		</Box>
	);
};

export default StudySessionFeedbackContainer;
